import React, { useEffect } from 'react';
import { Button, TextField, FormControlLabel, Checkbox, Link, Typography, Container, Card, CardContent, InputAdornment, Box, Alert } from '@mui/material';
import { EmailOutlined, LockOutlined, ShieldOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { closeAlert } from '../redux/slices/alerts';
import { logIn, setForgotPassword, setNewRegistration, clearMfa } from '../redux/slices/auth';
import { verify2FA } from '../redux/ducks/twoFactor';
import { getConfigValue } from '../redux/slices/config';
import Message from '../components/Message';
import styled from '@emotion/styled';
import { useBrandName } from '../utils/brandUtils';

const StyledContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
  minHeight: '100vh',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.up('sm')]: {
    width: '475px',
    margin: '0 auto',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const StyledCardRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2),
  width: '100%',
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(2, 3),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const StyledCopyright = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const StyledFirstTimeRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(1),
  width: '100%',
}));

const StyledFirstTimeText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const StyledInlineAlert = styled(Alert)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
}));

const TwoFactorHelper = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const TwoFactorToggleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  width: '100%',
}));

function Login() {
  const dispatch = useDispatch();
  const brandName = useBrandName();
  const alerts = useSelector(state => state.alerts);
  const mfaPending = useSelector(state => state.auth.mfaPending);
  const mfaToken = useSelector(state => state.auth.mfaToken);
  const mfaError = useSelector(state => state.auth.mfaError);
  const inlineError = alerts.alertOpen && alerts.alertSeverity === 'error' ? alerts.alertMessage : null;
  const [formData, setFormData] = React.useState({});
  const [checked, setChecked] = React.useState(false);
  const [selfRegister, setSelfRegister] = React.useState(false);
  const [twoFactorCode, setTwoFactorCode] = React.useState('');
  const [useBackupCode, setUseBackupCode] = React.useState(false);
  const [rememberDevice, setRememberDevice] = React.useState(false);

  useEffect(() => {
    dispatch(getConfigValue({ key: 'systemConfig', value: 'selfRegister', callback: (data) => {
      setSelfRegister(data?.value ?? false);
    } }));
  }, [dispatch]);

  const handleRememberChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleFormChange = (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [event.target.name]: value,
    });
  };

  const startForgotPassword = (event) => {
    const email = formData.email?.trim();
    dispatch(setForgotPassword({ step: 1, email: email || '', reason: null }));
  };

  const startFirstTimeSignIn = (event) => {
    const email = formData.email?.trim();
    dispatch(setForgotPassword({ step: 1, email: email || '', reason: 'FIRST_TIME' }));
  };

  const startNewRegistration = (event) => {
    dispatch(setNewRegistration({ step: 1 }));
  };

  const handleLogin = (event) => {
    event.preventDefault();
    dispatch(closeAlert());
    dispatch(logIn({ email: formData.email, password: formData.password, remember: checked }));
  };

  const handleVerify2FA = (event) => {
    event.preventDefault();
    if (!twoFactorCode.trim()) return;
    dispatch(verify2FA({ mfaToken, code: twoFactorCode.trim(), rememberDevice }));
  };

  const cancelTwoFactor = () => {
    setTwoFactorCode('');
    setRememberDevice(false);
    setUseBackupCode(false);
    setFormData({});
    setChecked(false);
    dispatch(clearMfa());
  };

  if (mfaPending) {
    return (
      <StyledContainer component="main">
        <StyledCard>
          <StyledCardContent component="form">
            <Typography variant="h4" component="h1" gutterBottom>
              Two-Factor Authentication
            </Typography>
            <TwoFactorHelper variant="body2">
              {useBackupCode
                ? 'Enter one of your backup codes to continue.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </TwoFactorHelper>
            <StyledTextField
              required
              fullWidth
              autoFocus
              id="twoFactorCode"
              name="twoFactorCode"
              label={useBackupCode ? 'Backup Code' : 'Authentication Code'}
              variant="outlined"
              type="text"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <ShieldOutlined />
                  </InputAdornment>
                ),
              }}
            />
            <TwoFactorToggleRow>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                  />
                }
                label="Remember this device for 30 days"
              />
              <StyledLink variant="body2" onClick={() => { setUseBackupCode((v) => !v); setTwoFactorCode(''); }}>
                {useBackupCode ? 'Use authenticator app code instead' : 'Use a backup code instead'}
              </StyledLink>
            </TwoFactorToggleRow>
            <StyledButton type="submit" fullWidth variant="contained" color="primary" onClick={handleVerify2FA}>
              Verify
            </StyledButton>
            {mfaError && (
              <StyledInlineAlert severity="error">
                {useBackupCode && /Invalid authentication code/i.test(mfaError) ? 'Invalid backup code' : mfaError}
              </StyledInlineAlert>
            )}
            <Typography variant="body2" align="center">
              <StyledLink onClick={cancelTwoFactor}>Back to sign in</StyledLink>
            </Typography>
          </StyledCardContent>
        </StyledCard>
        <StyledCopyright variant="body2" color="textSecondary" align="center">
          {"Copyright © "}
          <Link color="inherit" href="https://pollux-mui.com/">
            {brandName}
          </Link>{" "}
          {new Date().getFullYear()}
          {"."}
        </StyledCopyright>
      </StyledContainer>
    );
  }

  return (
      <StyledContainer component="main">
          <StyledCard>
              <StyledCardContent component="form">
                  <Typography variant="h4" component="h1" gutterBottom>
                      Sign In
                  </Typography>
                  <Message inline />
                  <StyledTextField
                      required
                      fullWidth
                      id="email"
                      variant="outlined"
                      name="email"
                      label="Email Address"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={formData.email || ''}
                      onChange={handleFormChange}
                      InputProps={{
                          endAdornment: (
                              <InputAdornment position="end">
                                  <EmailOutlined />
                              </InputAdornment>
                          ),
                      }}
                  />
                  <StyledTextField
                      required
                      fullWidth
                      id="password"
                      variant="outlined"
                      name="password"
                      label="Password"
                      type="password"
                      autoComplete="current-password"
                      value={formData.password || ''}
                      onChange={handleFormChange}
                      InputProps={{
                          endAdornment: (
                              <InputAdornment position="end">
                                  <LockOutlined />
                              </InputAdornment>
                          ),
                      }}
                  />
                  <StyledCardRow>
                    <FormControlLabel control={<Checkbox value="remember" color="primary" name="remember" checked={checked} onChange={handleRememberChange} />} label="Remember me" />
                    <StyledLink variant="body2" onClick={startForgotPassword}>Forgot your password</StyledLink>
                  </StyledCardRow>
                  <StyledFirstTimeRow>
                    <StyledFirstTimeText variant="body2">
                      First time here? <StyledLink variant="body2" onClick={startFirstTimeSignIn}>Email me a sign-in code</StyledLink>
                    </StyledFirstTimeText>
                  </StyledFirstTimeRow>
                  <StyledButton type="submit" fullWidth variant="contained" color="primary" onClick={handleLogin}>
                      Sign In
                  </StyledButton>
                  {inlineError && (
                      <StyledInlineAlert severity="error">{inlineError}</StyledInlineAlert>
                  )}
                  {selfRegister && (
                    <Typography variant="body2" align="center">
                        Don't have an account? <StyledLink onClick={startNewRegistration}>Create one</StyledLink>
                    </Typography>
                  )}
              </StyledCardContent>
          </StyledCard>

          <StyledCopyright variant="body2" color="textSecondary" align="center">
              {"Copyright © "}
              <Link color="inherit" href="https://pollux-mui.com/">
                  {brandName}
              </Link>{" "}
              {new Date().getFullYear()}
              {"."}
          </StyledCopyright>
      </StyledContainer>
  );
}

export default Login;
